import React, { FC, memo, useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { Box, Flex } from '..';
import { DIAGRAM_SOFTWARE_COLORS, ELEMENT_TYPE } from '../../constants/diagram';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { removeDiagramElements } from '../../redux/slices/diagramSlice';
import { getColor } from '../../styles/mixins';
import { cloneObject } from '../../utils';

import {
  Circle,
  ColumnNodeStyle,
  DeleteIcon,
  DeleteNode,
  FieldPanel,
  sourceHandleStyleA,
  targetHandleStyleA,
} from './ColumnNode.styled';

interface FieldBulletProps {
  selected: boolean;
  constraintColor: string;
}

const FieldBullet = ({
  selected,
  constraintColor,
}: FieldBulletProps): JSX.Element => {
  let colorProp = '';
  if (selected) colorProp = constraintColor;
  return <Circle color={colorProp}></Circle>;
};

const ColumnNode: FC<NodeProps> = ({ id, data, isConnectable, selected }) => {
  const dispatch = useAppDispatch();
  const diagram = useAppSelector((state) => state.diagramR);
  const nodeColor = DIAGRAM_SOFTWARE_COLORS[diagram.diagram_software_type];
  const [showDeleteColumnIcon, setShowDeleteColumnIcon] = useState(false);

  // Delete node
  const onDeleteNode = () => {
    if (isConnectable) {
      const columnElement = cloneObject(data);
      columnElement.type = ELEMENT_TYPE.COLUMN;
      columnElement.id = id;
      dispatch(removeDiagramElements([columnElement]));
    }
  };

  return (
    <>
      <Handle
        type="target"
        id="ColumnTarget"
        position={Position.Top}
        style={targetHandleStyleA}
      />
      <Box>
        <DeleteNode
          onClick={onDeleteNode}
          onMouseOver={() =>
            isConnectable ? setShowDeleteColumnIcon(true) : null
          }
          onMouseLeave={() =>
            isConnectable ? setShowDeleteColumnIcon(false) : null
          }
        >
          <DeleteIcon showDeleteColumnIcon={showDeleteColumnIcon} />
        </DeleteNode>

        <ColumnNodeStyle
          selectedNode={selected}
          onMouseOver={() =>
            isConnectable ? setShowDeleteColumnIcon(true) : null
          }
          onMouseLeave={() =>
            isConnectable ? setShowDeleteColumnIcon(false) : null
          }
        >
          <Flex justifyContent="center" py="8px">
            {data.name}
          </Flex>
        </ColumnNodeStyle>

        <FieldPanel
          panelColor={nodeColor}
          onMouseOver={() =>
            isConnectable ? setShowDeleteColumnIcon(true) : null
          }
          onMouseLeave={() =>
            isConnectable ? setShowDeleteColumnIcon(false) : null
          }
        >
          <Flex justifyContent="space-between" px="8px" py="4px">
            <FieldBullet
              selected={data.constraints.PRIMARY_KEY}
              constraintColor={getColor('green')}
            />
            <FieldBullet
              selected={data.constraints.UNIQUE}
              constraintColor={getColor('cyan')}
            />
            <FieldBullet
              selected={data.constraints.NOT_NULL}
              constraintColor={getColor('red')}
            />
            <FieldBullet
              selected={data.constraints.AUTO_INCREMENT}
              constraintColor={getColor('yellow')}
            />
            <FieldBullet
              selected={data.constraints.FOREIGN_KEY}
              constraintColor={getColor('purple')}
            />
          </Flex>
        </FieldPanel>
      </Box>
      <Handle
        type="source"
        position={Position.Right}
        id={ELEMENT_TYPE.FOREIGN_EDGE}
        style={sourceHandleStyleA}
        isConnectable={isConnectable}
      />
    </>
  );
};

export default memo(ColumnNode);
